import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '../client/errors';
import { queryKeys } from '../client/query-client';
import { BillingPeriod } from '../checkout/contracts';
/**
 * `GET /api/subscriptions/mine` — the caller's own subscriptions.
 *
 * A member-facing read scoped to the token. **Empty is a valid answer** — a
 * seat holder on an employer's plan reads `{"items":[]}` here because a
 * business subscription lives against the organisation, not the member (see
 * `session/session.ts`). That is why access is decided by `/entitlement`; this
 * read exists for the manage-my-own screens, which are the only place a
 * subscription id is needed to POST against.
 */
export const PendingPlanChange = z.object({
    planId: z.string().uuid(),
    planKey: z.string(),
    planName: z.string(),
    planVersionId: z.string().uuid(),
    revision: z.number().int(),
    priceCents: z.number().int(),
    currency: z.string(),
    period: BillingPeriod,
    effectiveAt: z.string().datetime().nullable(),
    requestedAt: z.string().datetime(),
});
export const SubscriptionStatus = z.enum([
    'TRIALING',
    'ACTIVE',
    'PAST_DUE',
    'CANCELED',
    'UNPAID',
    'INCOMPLETE',
    'INCOMPLETE_EXPIRED',
    'PAUSED',
]);
/**
 * The price revision a subscription is on — what it is billed, and how often.
 *
 * Declared because the plan screen needs `period`. It used to read the cadence
 * from the key's prefix (`monthly-…`), and the keys migrated from WordPress
 * look like `wp-plateforme-de-formations-la-boussole-du--month1-4900`: they
 * fell through to Annual, and a Monthly member saw Annual marked as theirs.
 * The period says it directly, whatever the key is.
 */
export const SubscriptionPlanVersion = z.object({
    id: z.string().uuid(),
    priceCents: z.number().int(),
    currency: z.string(),
    /*
     * `LIFETIME` as well as the three cadences the catalogue sells. The contract
     * allows it and a member can hold one, and this is required: a narrower enum
     * would fail the parse of the whole `/subscriptions/mine` read for that
     * member — every billing screen, not just the cadence label. The shared
     * `BillingPeriod` stays narrow on purpose; it is what the funnel sells.
     */
    period: z.enum([...BillingPeriod.options, 'LIFETIME']),
});
/** Mirrors `Subscription` on the backend. */
export const Subscription = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid().nullable(),
    organisationId: z.string().uuid().nullable(),
    status: SubscriptionStatus,
    seats: z.number().int(),
    planVersion: SubscriptionPlanVersion,
    planId: z.string().uuid(),
    planKey: z.string(),
    planName: z.string(),
    currentPeriodStart: z.string().datetime().nullable(),
    currentPeriodEnd: z.string().datetime().nullable(),
    trialEndsAt: z.string().datetime().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    canceledAt: z.string().datetime().nullable(),
    pendingPlanChange: PendingPlanChange.nullable(),
    stripeSubscriptionId: z.string().nullable(),
    entitled: z.boolean(),
    createdAt: z.string().datetime(),
});
export const MySubscriptionsResponse = z.object({
    items: z.array(Subscription),
});
const SUBSCRIPTIONS_QUERY_KEY = ['subscriptions', 'mine'];
export function useMySubscriptions(http) {
    return useQuery({
        queryKey: SUBSCRIPTIONS_QUERY_KEY,
        queryFn: () => http.get('/subscriptions/mine', { schema: MySubscriptionsResponse }),
        staleTime: 30_000,
    });
}
/**
 * `POST /api/subscriptions/mine/{id}/change-plan` — schedule a plan move at
 * the next renewal.
 *
 * The contract takes `planId` (uuid), not a key — a client naming a revision
 * could name a retired one, so the server resolves the sellable revision from
 * the plan itself. Nothing is charged and nothing is prorated: the current
 * `planVersion` is untouched until `currentPeriodEnd` arrives.
 *
 * Refusals to surface: SUBSCRIPTION_NOT_FOUND, SUBSCRIPTION_ALREADY_CANCELED,
 * SUBSCRIPTION_NOT_LIVE, SUBSCRIPTION_CANCELLING, SUBSCRIPTION_PLAN_UNCHANGED,
 * PLAN_NOT_FOUND, PLAN_NOT_SELLABLE.
 *
 * On success we invalidate `subscriptions.mine` and the shared `entitlement`
 * key so any "current plan" chip elsewhere re-reads what the server now holds.
 */
export const ChangePlan = z.object({ planId: z.string().uuid() });
export function useChangeMySubscriptionPlan(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, planId }) => http.post(`/subscriptions/mine/${id}/change-plan`, ChangePlan.parse({ planId }), {
            schema: Subscription,
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: queryKeys.entitlementAll });
        },
    });
}
/**
 * Cancel a scheduled switch; the member stays on their current plan.
 *
 * The only way back. Choosing the current plan again is refused as
 * `SUBSCRIPTION_PLAN_UNCHANGED`, because it is the plan they are on.
 */
export function useWithdrawPlanChange(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => http.post(`/subscriptions/mine/${id}/change-plan/withdraw`, {}, { schema: Subscription }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: queryKeys.entitlementAll });
        },
    });
}
/**
 * Which of a member's subscriptions the billing screens are about.
 *
 * ── Why this is not `items[0]` ────────────────────────────────────────────
 * It was, and that is the bug reported on 1 October: « when I am trying to
 * upgrade my plan everything is disabled ». The contract promises no ordering,
 * and members DO hold more than one row — on the local database alone, one
 * member has two cancelled subscriptions and an e2e account has two active
 * ones. When a dead or WordPress-migrated row sorts first, the screen reads
 * ITS missing `stripeSubscriptionId`, decides the plan cannot be changed here,
 * and disables a switch the member is perfectly entitled to make.
 *
 * The order of preference:
 *   1. a live subscription that can actually be switched (Stripe bills it),
 *   2. any live subscription — so a migrated member still reads their real
 *      plan and renewal date, and gets the honest "not here yet" explanation,
 *   3. whatever exists, so a fully cancelled account shows its last one
 *      rather than an empty screen.
 *
 * One rule for every billing screen, so the cadence, the price and the pay
 * path all describe the same subscription instead of three different ones.
 */
const LIVE_STATUSES = ['ACTIVE', 'TRIALING', 'PAST_DUE', 'UNPAID'];
export function isLiveSubscription(subscription) {
    return LIVE_STATUSES.includes(subscription.status);
}
export function currentSubscription(subscriptions) {
    if (!subscriptions || subscriptions.length === 0)
        return null;
    return subscriptions.find(isLiveSubscription) ?? subscriptions[0] ?? null;
}
/**
 * Why a plan change or a withdrawal was refused, as a key.
 *
 * Only the refusals where "try again" would be a lie get their own words: a
 * plan or payment setup that is not ready on our side. Everything else keeps
 * the retry line it always had.
 *
 * `SUBSCRIPTION_NOT_BILLED_BY_STRIPE` was here and is gone — the backend
 * retired the code when it made a plan change work for the subscriptions this
 * platform bills itself (reply 20, 2 Oct). It is out of the contract, so a
 * `case` for it would stop compiling at the next regeneration.
 */
export function planChangeRefusalKey(error) {
    if (!isApiError(error))
        return 'billing.switchError';
    switch (error.code) {
        case 'PLAN_HAS_NO_STRIPE_PRICE':
        case 'STRIPE_NOT_CONFIGURED':
            return 'billing.switchUnavailable';
        default:
            return 'billing.switchError';
    }
}
//# sourceMappingURL=subscriptions.js.map